import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import UserSelector from './UserSelector';
import { Modal, Button } from './ui';
import { createShare, getCultureGenesisUsers } from '../services/api';
import { CultureGenesisUser } from '../types/sharing';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  messageIds: string[];
  sessionId: string;
  onShareSuccess: (recipientName: string) => void;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  messageIds,
  sessionId,
  onShareSuccess
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<CultureGenesisUser[]>([]);

  // Load users and reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchUsers = async () => {
        try {
          const response = await getCultureGenesisUsers();
          setUsers(response.users || []);
        } catch (err) {
          console.error('Failed to load users:', err);
        }
      };
      fetchUsers();
    } else {
      // Reset state when modal closes
      setSelectedUserId(null);
      setNotes('');
      setError(null);
      setUsers([]);
    }
  }, [isOpen]);

  const handleShare = async () => {
    if (!selectedUserId) {
      setError('Please select a recipient');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await createShare(
        selectedUserId,
        sessionId,
        messageIds,
        notes || undefined
      );

      const selectedUser = users.find(u => u.userId === selectedUserId);
      const recipientName = selectedUser?.displayName || selectedUser?.email || 'team member';

      onShareSuccess(recipientName);
    } catch (err: any) {
      console.error('Failed to share:', err);
      setError(err.message || 'Failed to share conversation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't allow dismissal (Esc / backdrop / ✕) while a share is in flight
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const styles: { [key: string]: React.CSSProperties } = {
    section: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: 'var(--fg-2)',
      marginBottom: 6,
    },
    textarea: {
      width: '100%',
      minHeight: '80px',
      padding: '10px 12px',
      border: '1px solid var(--border-2)',
      borderRadius: 'var(--radius-md)',
      fontSize: 14,
      color: 'var(--fg-1)',
      fontFamily: 'inherit',
      resize: 'vertical',
      boxSizing: 'border-box',
      backgroundColor: '#FFFFFF',
    },
    info: {
      fontSize: 12.5,
      color: 'var(--fg-3)',
      marginTop: '0.5rem',
    },
    error: {
      padding: '0.75rem 1rem',
      backgroundColor: 'var(--danger-soft)',
      border: '1px solid var(--cg-terra-200)',
      color: 'var(--danger)',
      borderRadius: 'var(--radius-md)',
      fontSize: 13,
      fontWeight: 500,
      marginBottom: '1rem',
    },
  };

  // Ensure document.body exists before rendering portal
  if (typeof document === 'undefined' || !document.body) {
    console.error('[ShareModal] document.body not available');
    return null;
  }

  // Use React Portal to render modal at document.body level
  // This breaks out of parent component CSS inheritance
  const modalContent = (
    <Modal
      open
      onClose={handleClose}
      title="Share Conversation"
      width={500}
      ariaLabel="Share conversation"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleShare}
            disabled={isLoading || !selectedUserId}
          >
            {isLoading ? 'Sharing...' : 'Share Conversation'}
          </Button>
        </>
      }
    >
      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.section}>
        <label style={styles.label}>Share with:</label>
        <UserSelector
          selectedUserId={selectedUserId}
          onSelectUser={(userId) => {
            setSelectedUserId(userId);
            setError(null);
          }}
        />
      </div>

      <div style={{ ...styles.section, marginBottom: 0 }}>
        <label style={styles.label}>Add note (optional):</label>
        <textarea
          style={styles.textarea}
          placeholder="Add a message for the recipient..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isLoading}
          maxLength={500}
        />
        <div style={styles.info}>
          Sharing {messageIds.length} message{messageIds.length !== 1 ? 's' : ''}
        </div>
      </div>
    </Modal>
  );

  // Render modal using React Portal at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ShareModal;
